/**
 * /ai/taxonomy — document types + routes CRUD.
 * Types drive the classifier's label set; routes map (doc_type, lender) →
 * extraction skill. A classified document matching no route goes through the
 * generic extractor into the unrouted queue.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Heading,
  HStack,
  Text,
  Button,
  Input,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Select,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Textarea,
  useDisclosure,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiPlus, FiRefreshCw, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { callDocai } from '../../hooks/useDocAI';

const EMPTY_TYPE = { type_id: '', name: '', description: '', signals: [], arive_categories: [], is_active: true };

export default function AITaxonomyPage() {
  const [types, setTypes] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editType, setEditType] = useState(null); // working copy for the modal
  const [signalsText, setSignalsText] = useState('');
  const [categoriesText, setCategoriesText] = useState('');
  const [newRoute, setNewRoute] = useState({ doc_type: '', lender: '', skill_name: '' });
  const [saving, setSaving] = useState(false);
  const typeModal = useDisclosure();
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tax, sk] = await Promise.all([
        callDocai('docaiTaxonomy', { action: 'list' }),
        callDocai('docaiSkills', { action: 'list' }),
      ]);
      setTypes(tax.types || []);
      setRoutes(tax.routes || []);
      setSkills((sk.skills || []).filter((s) => !s.name.startsWith('_')));
    } catch (err) {
      toast({ title: 'Taxonomy load failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openTypeModal = (t) => {
    const working = t ? { ...t } : { ...EMPTY_TYPE };
    setEditType(working);
    setSignalsText((working.signals || []).join('\n'));
    setCategoriesText((working.arive_categories || []).join('\n'));
    typeModal.onOpen();
  };

  const saveType = async () => {
    setSaving(true);
    try {
      await callDocai('docaiTaxonomy', {
        action: 'upsertType',
        type: {
          ...editType,
          signals: signalsText.split('\n').map((s) => s.trim()).filter(Boolean),
          arive_categories: categoriesText.split('\n').map((s) => s.trim()).filter(Boolean),
        },
      });
      toast({ title: 'Type saved', status: 'success', duration: 2500 });
      typeModal.onClose();
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const deleteType = async (typeId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete type "${typeId}"? Routes using it will stop matching.`)) return;
    try {
      await callDocai('docaiTaxonomy', { action: 'deleteType', typeId });
      toast({ title: 'Type deleted', status: 'success', duration: 2500 });
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 6000 });
    }
  };

  const addRoute = async () => {
    if (!newRoute.doc_type || !newRoute.skill_name) {
      toast({ title: 'doc type and skill are required', status: 'warning', duration: 3000 });
      return;
    }
    setSaving(true);
    try {
      await callDocai('docaiTaxonomy', { action: 'upsertRoute', route: { ...newRoute, lender: newRoute.lender || null } });
      toast({ title: 'Route saved', status: 'success', duration: 2500 });
      setNewRoute({ doc_type: '', lender: '', skill_name: '' });
      load();
    } catch (err) {
      toast({ title: 'Route save failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const deleteRoute = async (routeId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this route? Documents of this type/lender will go to the unrouted queue.')) return;
    try {
      await callDocai('docaiTaxonomy', { action: 'deleteRoute', routeId });
      toast({ title: 'Route deleted', status: 'success', duration: 2500 });
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 6000 });
    }
  };

  if (loading && !types.length) {
    return <Center h="60vh"><Spinner size="xl" color="blue.400" /></Center>;
  }

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={5}>
        <Heading size="md">Taxonomy</Heading>
        <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" onClick={load} isLoading={loading}>
          Refresh
        </Button>
      </HStack>

      {/* Routes */}
      <HStack justify="space-between" mb={3}>
        <Heading size="sm">Routes (doc type + lender → skill)</Heading>
      </HStack>
      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" overflow="hidden" mb={3}>
        <Table size="sm">
          <Thead><Tr><Th>Doc type</Th><Th>Lender</Th><Th>Skill</Th><Th>Active</Th><Th w="60px" /></Tr></Thead>
          <Tbody>
            {routes.map((r) => (
              <Tr key={r.id}>
                <Td fontSize="xs" fontFamily="mono">{r.doc_type}</Td>
                <Td fontSize="xs">{r.lender || <Text as="span" color="gray.400">any</Text>}</Td>
                <Td fontSize="xs" fontFamily="mono">{r.skill_name}</Td>
                <Td fontSize="xs">{r.is_active ? '✓' : '—'}</Td>
                <Td>
                  <IconButton
                    aria-label="Delete route"
                    icon={<FiTrash2 />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => deleteRoute(r.id)}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <HStack mb={8} spacing={2} flexWrap="wrap">
        <Select
          size="sm"
          w="240px"
          placeholder="doc type"
          value={newRoute.doc_type}
          onChange={(e) => setNewRoute((p) => ({ ...p, doc_type: e.target.value }))}
        >
          {types.map((t) => (
            <option key={t.type_id} value={t.type_id}>{t.type_id}</option>
          ))}
        </Select>
        <Input
          size="sm"
          w="160px"
          placeholder="lender (blank = any)"
          value={newRoute.lender}
          onChange={(e) => setNewRoute((p) => ({ ...p, lender: e.target.value }))}
        />
        <Select
          size="sm"
          w="260px"
          placeholder="skill"
          value={newRoute.skill_name}
          onChange={(e) => setNewRoute((p) => ({ ...p, skill_name: e.target.value }))}
        >
          {skills.map((s) => (
            <option key={s.name} value={s.name}>{s.name}{s.is_draft ? ' (draft)' : ''}</option>
          ))}
        </Select>
        <Button size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={addRoute} isLoading={saving}>
          Add route
        </Button>
      </HStack>

      {/* Types */}
      <HStack justify="space-between" mb={3}>
        <Heading size="sm">Document types ({types.length})</Heading>
        <Button size="sm" leftIcon={<FiPlus />} onClick={() => openTypeModal(null)}>
          New type
        </Button>
      </HStack>
      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" overflow="hidden">
        <Table size="sm">
          <Thead><Tr><Th>Type</Th><Th>Name</Th><Th>Signals</Th><Th>Arive categories</Th><Th w="90px" /></Tr></Thead>
          <Tbody>
            {types.map((t) => (
              <Tr key={t.type_id}>
                <Td fontSize="xs" fontFamily="mono">{t.type_id}</Td>
                <Td fontSize="xs">{t.name}</Td>
                <Td maxW="320px">
                  <Text fontSize="10px" color="gray.500" noOfLines={2}>{(t.signals || []).join(' · ')}</Text>
                </Td>
                <Td maxW="220px">
                  <HStack spacing={1} flexWrap="wrap">
                    {(t.arive_categories || []).slice(0, 3).map((c) => (
                      <Tag key={c} size="sm" fontSize="10px">{c}</Tag>
                    ))}
                    {(t.arive_categories || []).length > 3 && (
                      <Text fontSize="10px" color="gray.500">+{t.arive_categories.length - 3}</Text>
                    )}
                  </HStack>
                </Td>
                <Td>
                  <HStack spacing={0}>
                    <IconButton aria-label="Edit type" icon={<FiEdit2 />} size="xs" variant="ghost" onClick={() => openTypeModal(t)} />
                    <IconButton aria-label="Delete type" icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" onClick={() => deleteType(t.type_id)} />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Type modal */}
      <Modal isOpen={typeModal.isOpen} onClose={typeModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editType?.type_id ? `Edit ${editType.type_id}` : 'New document type'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editType && (
              <Box>
                <Text fontSize="xs" mb={1} color="gray.500">type_id (snake_case, immutable key)</Text>
                <Input
                  size="sm"
                  mb={3}
                  value={editType.type_id}
                  onChange={(e) => setEditType((p) => ({ ...p, type_id: e.target.value.toLowerCase() }))}
                />
                <Text fontSize="xs" mb={1} color="gray.500">Display name</Text>
                <Input
                  size="sm"
                  mb={3}
                  value={editType.name}
                  onChange={(e) => setEditType((p) => ({ ...p, name: e.target.value }))}
                />
                <Text fontSize="xs" mb={1} color="gray.500">Description (helps the classifier)</Text>
                <Textarea
                  size="sm"
                  mb={3}
                  rows={2}
                  value={editType.description || ''}
                  onChange={(e) => setEditType((p) => ({ ...p, description: e.target.value }))}
                />
                <Text fontSize="xs" mb={1} color="gray.500">Signals — one per line (phrases that identify this type)</Text>
                <Textarea size="sm" mb={3} rows={4} value={signalsText} onChange={(e) => setSignalsText(e.target.value)} />
                <Text fontSize="xs" mb={1} color="gray.500">Arive categories — one per line (for corpus imports)</Text>
                <Textarea size="sm" rows={3} value={categoriesText} onChange={(e) => setCategoriesText(e.target.value)} />
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={typeModal.onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={saveType} isLoading={saving}>Save type</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
